import { Alert as ChakraAlert } from "@chakra-ui/react";
import { PropsWithChildren, createContext, useContext } from "react";
import Icon from "@ui-kit/icons/Icon.web";
import type { IconProps } from "@ui-kit/icons/Icon.props";
import type { AlertSharedProps } from "./Alert.props";

type ColorName =
  | "gray"
  | "red"
  | "green"
  | "yellow"
  | "blue"
  | "purple"
  | "pink"
  | "indigo"
  | "primary"
  | "secondary";

const statusToColor: Record<string, ColorName> = {
  error: "red",
  warning: "yellow",
  success: "green",
  info: "blue",
};

const statusToIcon: Record<string, string> = {
  error: "Error",
  warning: "Warning",
  success: "TickCircle",
  info: "Info",
};

type AlertContextType = {
  status?: "error" | "warning" | "success" | "info";
  colorPalette?: ColorName;
};

const AlertContext = createContext<AlertContextType>({});

function createAlertStyles(
  finalColor: ColorName | undefined,
  showBorder: boolean
) {
  if (!finalColor) {
    return {};
  }

  return {
    backgroundColor: `${finalColor}.100`,
    color: `${finalColor}.800`,
    borderColor: showBorder ? `${finalColor}.500` : undefined,
    borderWidth: showBorder ? "1px" : undefined,
    borderStyle: showBorder ? "solid" : undefined,
  };
}

function AlertTitle({ children }: PropsWithChildren): JSX.Element {
  return <ChakraAlert.Title fontWeight="bold">{children}</ChakraAlert.Title>;
}

function AlertDescription({ children }: PropsWithChildren): JSX.Element {
  return <ChakraAlert.Description>{children}</ChakraAlert.Description>;
}

function AlertIcon({
  icon,
  variant,
  ...props
}: {
  icon?: string;
  variant?: "outline" | "solid";
} & IconProps): JSX.Element {
  const { status, colorPalette } = useContext(AlertContext);

  // Status takes precedence over color prop
  const finalColor = status ? statusToColor[status] : colorPalette;

  // Custom icon takes precedence over status icon
  const statusIcon = status ? statusToIcon[status] : null;
  const finalIcon = icon || statusIcon;

  if (!finalIcon) {
    // eslint-disable-next-line no-console
    console.warn(
      "Alert.Icon requires either 'status', 'colorPalette', or 'icon' prop"
    );
    return <></>;
  }

  return (
    <Icon
      name={finalIcon}
      outline={variant !== "solid"}
      color={finalColor ? `${finalColor}.500` : undefined}
      {...(finalColor && { colorPalette: finalColor })}
      w="element.lg"
      h="element.lg"
      marginRight="md"
      {...props}
    />
  );
}

function Alert({
  children,
  status,
  colorPalette,
  showBorder = false,
  alignItems,
  borderRadius = "md",
  justifyContent,
  width,
  mb,
  mt,
  ml,
  mr,
  ...props
}: AlertProps): JSX.Element {
  // Status takes precedence over color prop
  const finalColor = status ? statusToColor[status] : colorPalette;

  if (!finalColor) {
    // eslint-disable-next-line no-console
    console.warn(
      "Alert component requires either 'status' or 'colorPalette' prop"
    );
  }

  const alertStyles = createAlertStyles(finalColor, showBorder);

  return (
    <AlertContext.Provider value={{ status, colorPalette }}>
      <ChakraAlert.Root
        borderRadius={borderRadius}
        alignItems={alignItems}
        justifyContent={justifyContent}
        width={width}
        mb={mb}
        mt={mt}
        ml={ml}
        mr={mr}
        {...alertStyles}
        {...props}
      >
        {children}
      </ChakraAlert.Root>
    </AlertContext.Provider>
  );
}

Alert.Title = AlertTitle;
Alert.Description = AlertDescription;
Alert.Icon = AlertIcon;

export default Alert;

export type AlertProps = AlertSharedProps;
